import { Component, Vue } from 'vue-property-decorator';
import './setting.component.scss';
import { extend } from 'vee-validate';
import { SettingStoreConst } from '@/common/store/setting.store';
import { namespace } from 'vuex-class';
import SettingService from '@/common/service/setting.service';
import AppConstants from '@/common/constants/app.constants';
import { LinkInductionRequest } from '@/common/model/request/link-induction-request.model';
import FlashMessage from '@/common/components/flash-message.component';
import { InductionResponse } from '@/common/model/response/induction-response.model';
import { ScanMode } from '@/common/enum/scan-mode.enum';
import { v4 as uuidv4 } from 'uuid';
import { MstDeviceType } from '@/common/enum/mst-device-type.enum';
import ConfirmLinkInduction from '@/common/components/link-induction-confirm-modal/confirm-link-induction';
import '@/common/components/link-induction-confirm-modal/confirm-link-induction.scss';
import LoadingComponent from '@/common/components/loading/loading.component';
import SelectComponent from '@/common/components/multiselect/select.component';
import { SortStoreConst } from '@/tsort/store/sort.store';
import { ScanSettingResponse } from '@/tsort/model/response/scan-setting-response.model';
import InductionService from '@/common/service/induction.service';

const SettingStore = namespace(SettingStoreConst.SETTING_STORE_MODULE_NAME);
const SortStore = namespace(SortStoreConst.SORT_STORE_MODULE_NAME);

extend('max', {
  validate(value, { length }) {
    return value.length <= length;
  },
  params: ['length'],
  message: '17文字以内で入力してください。'
});

@Component({})
export default class SettingComponent extends Vue {
  private settingService = new SettingService();
  private inductionService = new InductionService();
  scanMode: ScanMode | null = null;
  inductionList = new Array<InductionResponse>();
  selectedInductionId: string | null = null;
  hasInductionError = false;
  typeScanMode: ScanMode | null = null;
  checkLinkInductionFlag = 1;
  isShow = false;
  messageConfigLinkInduction = '';
  isLoading = false;

  @SettingStore.Getter
  externalDeviceId!: string;

  @SettingStore.Getter
  deviceType!: MstDeviceType;

  @SettingStore.Getter
  inductionId!: string;

  @SettingStore.Action
  updateExternalDeviceId!: (value: string) => void;

  @SettingStore.Action
  updateDeviceId!: (value: string) => void;

  @SettingStore.Action
  updateInductionId!: (value: string) => void;

  @SettingStore.Action
  updateInductionName!: (value: string) => void;

  @SettingStore.Action
  updateInductionBreakingFlag!: (value: boolean) => void;

  @SortStore.Getter
  scanSetting!: ScanSettingResponse;

  async getInductionSettings(): Promise<void> {
    let inductionListResponse;
    try {
      inductionListResponse = await this.settingService.getInductions();
    } catch (error) {
      return;
    }
    if (!inductionListResponse) {
      return;
    }
    const { mstInduction } = inductionListResponse;
    if (!mstInduction?.length) {
      return;
    }
    this.inductionList = mstInduction;
  }

  async mounted(): Promise<void> {
    if (!this.externalDeviceId) {
      this.updateExternalDeviceId(`Ta${uuidv4()}`);
    }
    await this.getScanMode();
    await this.getInductionSettings();
    if (this.inductionId) {
      this.getLinkInduction();
    }
  }

  // インダクションリンクの確認モダールを表示する
  private showModal() {
    this.isShow = true;
  }

  // インダクションリンクの確認モダールをクローズする
  private async onClickExitBtn() {
    this.getLinkInduction();
    this.isShow = false;
  }

  // インダクションリンクを確認する
  private async onClickSubmitBtn() {
    this.onChangeLinkInduction(false);
    this.isShow = false;
  }

  /** ラベルカスタムする
   * @param inductionLabel 誘導ラベル
   * @return {string} インダクション名称またインダクションID
   */
  private customLabelInduction(inductionLabel: string) {
    const induction = this.inductionList.find((induction) => induction.inductionId === inductionLabel);
    return induction?.inductionName || induction?.inductionId;
  }

  async getLinkInduction(): Promise<void> {
    const dataResponse = await this.settingService.getLinkInduction(this.externalDeviceId, AppConstants.DEVICE_TYPE);
    if (dataResponse && dataResponse.mstInduction) {
      this.selectedInductionId = dataResponse.mstInduction.inductionId;
      this.updateInductionName(dataResponse.mstInduction.inductionName);
      this.updateDeviceId(dataResponse.mstInduction.deviceId);
    } else {
      this.selectedInductionId = null;
    }
  }

  public async getScanMode(): Promise<void> {
    const { scanMode } = await this.settingService.getScanMode();
    this.typeScanMode = scanMode;
    this.scanMode = scanMode;
  }

  async onChangeLinkInduction(isConfirm = true): Promise<void> {
    const inductionId = this.selectedInductionId as string;
    const externalDeviceId = this.externalDeviceId;
    const type = this.deviceType;
    try {
      this.isLoading = true;
      /** APIを呼び出してデバイスが存在しない場合は作成し、Inductionとリンクします */
      this.hasInductionError = false;
      const linkInductionRequest: LinkInductionRequest = {
        inductionId,
        externalDeviceId,
        type
      };

      const checkDeviceLinkedWithInductionResponse = await this.settingService.checkDeviceLinkedWithInduction(
        linkInductionRequest
      );
      const confirmMsg = checkDeviceLinkedWithInductionResponse?.confirmMsg;
      if (isConfirm && confirmMsg) {
        this.messageConfigLinkInduction = confirmMsg;
        this.showModal();
        return;
      }
      const linkInductionResponse = await this.settingService.postLinkInductionDevice(linkInductionRequest);
      /** サーバーから返されたデバイスIDを後で使用するためにストレージに保存してから、設定画面に戻ります。 */
      this.updateDeviceId(linkInductionResponse.deviceId);
      this.updateInductionId(inductionId);
      this.inductionList
        .filter((item) => item.inductionId === inductionId)
        .map((items) => {
          this.updateInductionName(items.inductionName);
          return;
        });
      // インダクションの状態を確認する
      const isRedirectToBreakInductionPage = await this.inductionService.isRedirectToBreakInductionPage(
        this.scanSetting?.inductionProcessingMode,
        inductionId
      );
      if (isRedirectToBreakInductionPage) {
        return;
      }
      FlashMessage.getInstance().showSuccessMessage(linkInductionResponse?.msg2);
    } catch (_e) {
      this.hasInductionError = true;
    } finally {
      this.isLoading = false;
    }
  }

  private async onClickChangeScanMode(scanMode: ScanMode, event: Event) {
    event.preventDefault();
    if (this.scanMode === scanMode) {
      return;
    }
    try {
      this.isLoading = true;
      const scanModePutResponse = await this.settingService.putScanMode(scanMode);

      if (!scanModePutResponse) {
        return;
      }
      FlashMessage.getInstance().showSuccessMessage('モード変更に成功しました。');
      this.typeScanMode = scanMode;
      this.scanMode = scanMode;
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    return (
      <b-tabs pills card active-nav-item-class="font-weight-bold custom-style-tabs ml-1" class="w-100 h-100">
        <b-tab title="t-Sortで商品仕分け" active no-body class="px-2 mx-2 mt-3 h-100">
          <div id="scroll-setting" class="bg-white sorter-content">
            <div class="row">
              <div class="col-11 ml-4 mt-4">
                <h4>
                  <strong>インダクション</strong>
                </h4>
              </div>
            </div>
            <div class="row mt-2">
              <div class="col margin-left-select mr-4">
                <SelectComponent
                  openDirection="bottom"
                  vModel={this.selectedInductionId}
                  custom-label={this.customLabelInduction}
                  options={this.inductionList.map((induction) => induction.inductionId)}
                  on-input={this.onChangeLinkInduction}
                  isError={this.hasInductionError}
                />
              </div>
            </div>
            <hr class="bg-secondary border-top border-2 mx-4 mt-5" />
            <div class="row">
              <div class="col-11 ml-4 mt-3">
                <h4>
                  <strong class="d-flex align-items-center">
                    スキャンモード<span class="required">必須</span>
                  </strong>
                </h4>
              </div>
            </div>
            <div class="row mt-2 ml-5">
              <div
                class={{
                  'col-auto pl-4 style-mode-scan scan-mode-radio-btn': true,
                  'radio-active': this.typeScanMode === ScanMode.REPRESENTATIVE_SCAN
                }}
                onClick={(event: Event) => {
                  this.onClickChangeScanMode(ScanMode.REPRESENTATIVE_SCAN, event);
                }}
              >
                <div class="row no-gutters">
                  <div class="col-1 d-flex pl-1 justify-content-end align-item-center m-auto">
                    <b-form-radio
                      vModel={this.typeScanMode}
                      name="scanMode"
                      value={ScanMode.REPRESENTATIVE_SCAN}
                    ></b-form-radio>
                  </div>
                  <div class="col d-flex flex-column justify-content-center text-size-14">
                    <strong>代表スキャン</strong>
                    <span>SKUあたり一度のみスキャン</span>
                  </div>
                </div>
              </div>
              <div
                class={{
                  'col-auto pl-3 style-mode-scan cursor-pointer': true,
                  'radio-active': this.typeScanMode === ScanMode.ONE_HUNDRED_PERCENT_SCAN
                }}
                onClick={(event: Event) => {
                  this.onClickChangeScanMode(ScanMode.ONE_HUNDRED_PERCENT_SCAN, event);
                }}
              >
                <div class="row no-gutters px-3">
                  <div class="col-1 d-flex justify-content-end align-item-center m-auto">
                    <b-form-radio
                      vModel={this.typeScanMode}
                      name="scanMode"
                      value={ScanMode.ONE_HUNDRED_PERCENT_SCAN}
                    ></b-form-radio>
                  </div>
                  <div class="col d-flex flex-column justify-content-center text-size-14">
                    <strong>全数スキャン</strong>
                    <span>全商品をスキャン</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Modal */}
          <ConfirmLinkInduction
            isShow={this.isShow}
            warningMessage={this.messageConfigLinkInduction}
            on-exitBtn={this.onClickExitBtn}
            on-acceptBtn={this.onClickSubmitBtn}
          />
          <LoadingComponent isActive={this.isLoading} />
        </b-tab>
        {/* <b-tab title="3D Sortで商品仕分け" disabled></b-tab>
        <b-tab title="t-Sortで商品ピッキング" disabled>
          <b-card-text>Tab 巡回仕分け（t-Sort）</b-card-text>
        </b-tab>
        <b-tab title="満杯シュートの箱を交換" disabled>
          <b-card-text>箱回収</b-card-text>
        </b-tab> */}
      </b-tabs>
    );
  }
}
